/**
 * FlotaLayout — Sidebar secundaria + tabs móviles para Mantenimiento.
 * Sub-secciones: Próximos · En curso · Histórico · Plantillas · Por embarcación.
 * Mismo patrón que CatalogoLayout / BookingLayout.
 */
import React, { useState, useMemo } from 'react';
import { getUserRole } from './shared/format';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip,
  Tabs, Tab, Badge, Fab, Tooltip,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import BuildIcon from '@mui/icons-material/Build';
import HistoryIcon from '@mui/icons-material/History';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import AddIcon from '@mui/icons-material/Add';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { useMantenimientos, useKpisFlota, categorizar } from './shared/useFlota';
import FlotaKpiBar from './shared/FlotaKpiBar';
import NuevoMantenimientoDialog from './NuevoMantenimientoDialog';

const ALL_SECTIONS = [
  { path: 'embarcaciones',  label: 'Embarcaciones',  icon: <DirectionsBoatIcon />,  color: '#0d9488', roles: ['comercial', 'admin', 'superadmin', 'miembro', 'operaciones'] },
  { path: 'proximos',       label: 'Próximos',       icon: <EventAvailableIcon />,  color: '#0ea5e9', roles: ['comercial', 'admin', 'superadmin', 'miembro', 'operaciones'] },
  { path: 'en-curso',       label: 'En curso',       icon: <BuildIcon />,           color: '#f59e0b', roles: ['comercial', 'admin', 'superadmin', 'miembro', 'operaciones'] },
  { path: 'historico',      label: 'Histórico',      icon: <HistoryIcon />,         color: '#64748b', roles: ['comercial', 'admin', 'superadmin', 'miembro', 'operaciones'] },
  { path: 'plantillas',     label: 'Plantillas',     icon: <ListAltIcon />,         color: '#8b5cf6', roles: ['comercial', 'admin', 'superadmin', 'miembro', 'operaciones'] },
  { path: 'combustible',    label: 'Combustible',    icon: <LocalGasStationIcon />, color: '#dc2626', roles: ['piloto', 'operaciones', 'admin', 'superadmin'] },
];

export default function FlotaLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mantenimientos, refetch, isLoading } = useMantenimientos();
  const { data: kpis, isLoading: kpisLoading } = useKpisFlota();
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const userRole = getUserRole();
  const SECTIONS = useMemo(
    () => ALL_SECTIONS.filter(s => s.roles.includes(userRole)),
    [userRole]
  );
  const isComercial = false;  // Acceso completo a Flota para todos los roles

  const counts = useMemo(() => {
    const c = categorizar(mantenimientos);
    return {
      proximos: c.proximos.length + c.vencidos.length,   // próximos incluye vencidos para resaltar
      'en-curso': c.enCurso.length,
      historico: c.completados.length,
    };
  }, [mantenimientos]);

  React.useEffect(() => {
    if (location.pathname === '/flota' || location.pathname === '/flota/') {
      navigate('/flota/embarcaciones', { replace: true });
    }
  }, [location.pathname, navigate]);

  const activeIdx = Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/flota/${s.path}`)));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar desktop */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Flota · Mantenimiento
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/flota/${s.path}`);
            const c = counts[s.path];
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/flota/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c > 0 && (
                  <Chip size="small" label={c}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: isActive ? s.color : '#e2e8f0',
                          color: isActive ? '#fff' : '#64748b' }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Plantillas crean mantenimientos recurrentes. Los vencidos se calculan
            automáticamente a partir de la fecha programada.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          mb: 2, mx: -2, px: 2,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: -16, zIndex: 5,
        }}>
          <Tabs value={activeIdx} onChange={(_, idx) => navigate(`/flota/${SECTIONS[idx].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5 },
              '& .MuiTabs-indicator': { backgroundColor: SECTIONS[activeIdx]?.color || '#0ea5e9', height: 3, borderRadius: 2 },
            }}>
            {SECTIONS.map(s => {
              const n = counts[s.path] || 0;
              return (
                <Tab key={s.path} icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={n} max={99}
                      sx={{ '& .MuiBadge-badge': {
                        bgcolor: s.color, color: '#fff',
                        fontSize: 9, height: 16, minWidth: 16, right: -6, top: 2,
                      } }}>
                      <span style={{ paddingRight: n > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  } />
              );
            })}
          </Tabs>
        </Box>

        {!isComercial && <FlotaKpiBar kpis={kpis} loading={kpisLoading} />}

        <Outlet context={{ mantenimientos, isLoading, refetch, onNuevo: () => setNuevoOpen(true) }} />
      </Box>

      {/* FAB Nuevo mantenimiento (no para comercial — solo consulta) */}
      {!isComercial && (
        <Tooltip title="Nuevo mantenimiento" placement="left">
          <Fab onClick={() => setNuevoOpen(true)}
            sx={{
              // Posicionado arriba del FAB del NaraChat (bottom: 20, right: 20).
              position: 'fixed', bottom: 96, right: 24, zIndex: 1301,
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              color: '#fff', boxShadow: '0 8px 24px rgba(13, 148, 136, 0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
            }}>
            <AddIcon />
          </Fab>
        </Tooltip>
      )}

      {nuevoOpen && (
        <NuevoMantenimientoDialog
          open={nuevoOpen}
          onClose={() => setNuevoOpen(false)}
          onCreated={() => { setNuevoOpen(false); refetch(); }}
        />
      )}
    </Box>
  );
}
